// Acquisition Attribution server functions.
// Public capture (no auth), authenticated linkage, admin-only reporting.
// Implements docs/growth/ACQUISITION-ATTRIBUTION-DESIGN.md.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  CLASSIFICATION_VERSION,
  classifyChannel,
  deriveChannel,
  type DerivedChannel,
} from "./acquisition-channel";
import {
  calculateFunnelRates,
  getReportWindows,
  type AcquisitionRange,
  type FunnelRates,
  type FunnelTotals,
  type ReportWindow,
} from "./acquisition-report";

// ---------------------------------------------------------------------------
// Capture (public, idempotent on anon_id)
// ---------------------------------------------------------------------------

const captureInput = z.object({
  anon_id: z.string().min(8).max(64).regex(/^[a-zA-Z0-9_-]+$/),
  first_referrer: z.string().max(2000).optional().nullable(),
  first_landing_path: z.string().max(2000).optional().nullable(),
  first_landing_url: z.string().max(2000).optional().nullable(),
  utm_source: z.string().max(255).optional().nullable(),
  utm_medium: z.string().max(255).optional().nullable(),
  utm_campaign: z.string().max(255).optional().nullable(),
  utm_term: z.string().max(255).optional().nullable(),
  utm_content: z.string().max(255).optional().nullable(),
});

function parseHost(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

export const captureAttribution = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => captureInput.parse(data))
  .handler(async ({ data }) => {
    const first_referrer_host = parseHost(data.first_referrer);

    const derived_channel: DerivedChannel = deriveChannel({
      utm_source: data.utm_source,
      utm_medium: data.utm_medium,
      first_referrer: data.first_referrer,
      first_referrer_host,
      first_landing_url: data.first_landing_url,
    });

    // ON CONFLICT (anon_id) DO NOTHING => first-touch immutability at insert time.
    // upsert with ignoreDuplicates = ON CONFLICT DO NOTHING (no failed transaction).
    const { error } = await supabaseAdmin
      .from("acquisition_attributions")
      .upsert({
        anon_id: data.anon_id,
        first_referrer: data.first_referrer ?? null,
        first_referrer_host,
        first_landing_path: data.first_landing_path ?? null,
        first_landing_url: data.first_landing_url ?? null,
        utm_source: data.utm_source ?? null,
        utm_medium: data.utm_medium ?? null,
        utm_campaign: data.utm_campaign ?? null,
        utm_term: data.utm_term ?? null,
        utm_content: data.utm_content ?? null,
        derived_channel,
        classification_version: CLASSIFICATION_VERSION,
      }, { onConflict: "anon_id", ignoreDuplicates: true });

    // 23505 = unique_violation. Expected when the anon_id was already captured.
    if (error && error.code !== "23505") {
      console.error("captureAttribution insert failed:", error);
      return { ok: false as const, error: "capture_failed" };
    }
    return { ok: true as const };
  });

// ---------------------------------------------------------------------------
// Linkage (authenticated; attaches user_id to the anonymous row)
// ---------------------------------------------------------------------------

const linkInput = z.object({
  anon_id: z.string().min(8).max(64).regex(/^[a-zA-Z0-9_-]+$/),
});

export const linkAttribution = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => linkInput.parse(data))
  .handler(async ({ data, context }) => {
    const userId = context.userId;

    // If this user already has any attribution row, skip (write-once).
    const { data: existing } = await supabaseAdmin
      .from("acquisition_attributions")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (existing) return { ok: true as const, linked: false as const };

    // Attach to the orphan anon row, if any. The DB unique-index on user_id
    // (where not null) + the immutability trigger keep this safe.
    const { data: updated, error } = await supabaseAdmin
      .from("acquisition_attributions")
      .update({ user_id: userId, linked_at: new Date().toISOString() })
      .eq("anon_id", data.anon_id)
      .is("user_id", null)
      .select("id")
      .maybeSingle();

    if (error) {
      console.error("linkAttribution update failed:", error);
      return { ok: false as const, error: "link_failed" };
    }

    // Seed the activation row so the user appears in admin reporting even
    // before they perform any activation event.
    await supabaseAdmin
      .from("acquisition_activations")
      .upsert({ user_id: userId }, { onConflict: "user_id", ignoreDuplicates: true });

    return { ok: true as const, linked: !!updated };
  });

// ---------------------------------------------------------------------------
// Admin reporting
// ---------------------------------------------------------------------------

export interface ChannelRow {
  derived_channel: string;
  classification_version: number;
  visits: number;
  signups: number;
  activated: number;
  first_subscriptions: number;
  active_subscribers: number;
}

export interface RecentSignupRow {
  user_id: string;
  display_name: string | null;
  first_seen_at: string;
  derived_channel: string;
  classification_version: number;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  first_referrer_host: string | null;
  first_landing_path: string | null;
  activated_at: string | null;
  first_subscription_at: string | null;
  current_subscription_status: string | null;
}

export interface AiSourceRow {
  /** Stable key for React lists, e.g. "ChatGPT". */
  source: string;
  /** All attribution rows (linked + anonymous) matching this AI source. */
  visits: number;
  /** Linked attribution rows (i.e. users who signed up). */
  signups: number;
  /** Linked users whose subscription is currently active or trialing. */
  members: number;
}

export interface LandingPageRow {
  path: string;
  visits: number;
  signups: number;
  members: number;
}

const AI_WATCHLIST = ["ChatGPT", "Gemini", "Claude", "Copilot", "Perplexity", "Grok"];

function normalizePath(p: string | null): string {
  if (!p) return "(unattributed path)";
  const base = p.split("?")[0].split("#")[0] || "/";
  return base.length > 1 ? base.replace(/\/+$/, "") : base;
}

type AttributionReportRow = {
  user_id: string | null;
  first_seen_at: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  first_referrer_host: string | null;
  first_landing_url: string | null;
  first_landing_path: string | null;
};

type ActivationReportRow = {
  user_id: string;
  activated_at: string | null;
  first_subscription_at: string | null;
  current_subscription_status: string | null;
};

async function fetchAttributionRows(window: ReportWindow) {
  const out: AttributionReportRow[] = [];
  const page = 1000;
  for (let from = 0; from < 200_000; from += page) {
    let q = supabaseAdmin
      .from("acquisition_attributions")
      .select("user_id, first_seen_at, utm_source, utm_medium, utm_campaign, first_referrer_host, first_landing_url, first_landing_path")
      .order("first_seen_at", { ascending: false })
      .range(from, from + page - 1);
    if (window.start) q = q.gte("first_seen_at", window.start);
    if (window.end) q = q.lt("first_seen_at", window.end);
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    out.push(...((data ?? []) as AttributionReportRow[]));
    if (!data || data.length < page) break;
  }
  return out;
}

export interface AcquisitionReport {
  range: AcquisitionRange;
  landing_pages: LandingPageRow[];
  total_visits: number;
  channels: ChannelRow[];
  totals: FunnelTotals;
  rates: FunnelRates;
  previous: {
    totals: FunnelTotals;
    rates: FunnelRates;
  } | null;
  recent: RecentSignupRow[];
  ai_sources: AiSourceRow[];
  classification_version_current: number;
}

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error || !data) {
    throw new Response("Forbidden: admin role required", { status: 403 });
  }
}

function currentChannel(r: AttributionReportRow): DerivedChannel {
  return classifyChannel({
    utm_source: r.utm_source,
    utm_medium: r.utm_medium,
    first_referrer_host: r.first_referrer_host,
    first_landing_url: r.first_landing_url ?? r.first_landing_path,
  }).channel;
}

function emptyTotals(): FunnelTotals {
  return {
    visits: 0,
    signups: 0,
    activated: 0,
    first_subscriptions: 0,
    active_subscribers: 0,
  };
}

function summarizeRows(
  rows: AttributionReportRow[],
  actMap: Map<string, ActivationReportRow>,
) {
  const totals = emptyTotals();
  const rollup = new Map<string, ChannelRow>();

  for (const row of rows) {
    const channel = currentChannel(row);
    const channelRow = rollup.get(channel) ?? {
      derived_channel: channel,
      classification_version: CLASSIFICATION_VERSION,
      visits: 0,
      signups: 0,
      activated: 0,
      first_subscriptions: 0,
      active_subscribers: 0,
    };

    totals.visits += 1;
    channelRow.visits += 1;
    if (row.user_id) {
      totals.signups += 1;
      channelRow.signups += 1;
      const activation = actMap.get(row.user_id);
      if (activation?.activated_at) {
        totals.activated += 1;
        channelRow.activated += 1;
      }
      if (activation?.first_subscription_at) {
        totals.first_subscriptions += 1;
        channelRow.first_subscriptions += 1;
      }
      if (
        activation?.current_subscription_status === "active" ||
        activation?.current_subscription_status === "trialing"
      ) {
        totals.active_subscribers += 1;
        channelRow.active_subscribers += 1;
      }
    }
    rollup.set(channel, channelRow);
  }

  return {
    totals,
    rates: calculateFunnelRates(totals),
    channels: Array.from(rollup.values()).sort(
      (a, b) => b.signups - a.signups || b.visits - a.visits,
    ),
  };
}

export const getAcquisitionReport = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ range: z.enum(["7d", "30d", "all"]).default("all") }).parse(data ?? {}),
  )
  .handler(async ({ context, data: input }): Promise<AcquisitionReport> => {
    await assertAdmin(context.supabase, context.userId);
    const range = input.range;
    const windows = getReportWindows(range);
    const [allRows, previousRows] = await Promise.all([
      fetchAttributionRows(windows.current),
      windows.previous ? fetchAttributionRows(windows.previous) : Promise.resolve([]),
    ]);
    const linked = allRows.filter(
      (row): row is AttributionReportRow & { user_id: string } => !!row.user_id,
    );
    const userIds = Array.from(
      new Set(
        [...allRows, ...previousRows]
          .map((row) => row.user_id)
          .filter((userId): userId is string => !!userId),
      ),
    );
    const { data: activations } = userIds.length
      ? await supabaseAdmin
          .from("acquisition_activations")
          .select(
            "user_id, activated_at, first_subscription_at, current_subscription_status",
          )
          .in("user_id", userIds)
      : { data: [] as ActivationReportRow[] };

    const { data: profiles } = userIds.length
      ? await supabaseAdmin
          .from("profiles")
          .select("id, display_name")
          .in("id", userIds)
      : { data: [] as Array<{ id: string; display_name: string | null }> };

    const actMap = new Map<string, ActivationReportRow>(
      (activations ?? []).map((a) => [a.user_id as string, a as ActivationReportRow]),
    );
    const profMap = new Map(
      (profiles ?? []).map((p) => [p.id as string, p.display_name as string | null]),
    );

    const summary = summarizeRows(allRows, actMap);
    const previousSummary = windows.previous
      ? summarizeRows(previousRows, actMap)
      : null;

    const recent: RecentSignupRow[] = linked.slice(0, 25).map((r) => {
      const act = actMap.get(r.user_id);
      return {
        user_id: r.user_id,
        display_name: profMap.get(r.user_id) ?? null,
        first_seen_at: r.first_seen_at,
        derived_channel: currentChannel(r),
        classification_version: CLASSIFICATION_VERSION,
        utm_source: r.utm_source,
        utm_medium: r.utm_medium,
        utm_campaign: r.utm_campaign,
        first_referrer_host: r.first_referrer_host,
        first_landing_path: r.first_landing_path,
        activated_at: act?.activated_at ?? null,
        first_subscription_at: act?.first_subscription_at ?? null,
        current_subscription_status: act?.current_subscription_status ?? null,
      };
    });

    // AI Discovery sub-source rollup. We include unlinked (anonymous) rows so
    // "visits" is a true traffic count, not just signups. Classify server-side
    // through classifyChannel so this surface and the capture/insert path
    // share one source of truth.
    const isMember = (uid: string | null) => {
      if (!uid) return false;
      const st = actMap.get(uid)?.current_subscription_status;
      return st === "active" || st === "trialing";
    };
    const lpRollup = new Map<string, LandingPageRow>();
    for (const r of allRows) {
      const path = normalizePath(r.first_landing_path);
      const row = lpRollup.get(path) ?? { path, visits: 0, signups: 0, members: 0 };
      row.visits += 1;
      if (r.user_id) row.signups += 1;
      if (isMember(r.user_id)) row.members += 1;
      lpRollup.set(path, row);
    }
    const landing_pages = Array.from(lpRollup.values())
      .sort((a, b) => b.signups - a.signups || b.visits - a.visits)
      .slice(0, 20);

    const aiRollup = new Map<string, AiSourceRow>();
    for (const name of AI_WATCHLIST) {
      aiRollup.set(name, { source: name, visits: 0, signups: 0, members: 0 });
    }
    for (const r of allRows ?? []) {
      const c = classifyChannel({
        utm_source: r.utm_source as string | null,
        utm_medium: r.utm_medium as string | null,
        first_referrer_host: r.first_referrer_host as string | null,
        first_landing_url: r.first_landing_url as string | null,
      });
      if (c.channel !== "ai_discovery" || !c.source) continue;
      const row =
        aiRollup.get(c.source) ??
        { source: c.source, visits: 0, signups: 0, members: 0 };
      row.visits += 1;
      const uid = r.user_id as string | null;
      if (uid) {
        row.signups += 1;
        const act = actMap.get(uid);
        if (
          act?.current_subscription_status === "active" ||
          act?.current_subscription_status === "trialing"
        ) {
          row.members += 1;
        }
      }
      aiRollup.set(c.source, row);
    }
    const ai_sources = Array.from(aiRollup.values()).sort((a, b) => b.visits - a.visits);

    return {
      range,
      landing_pages,
      total_visits: allRows.length,
      channels: summary.channels,
      totals: summary.totals,
      rates: summary.rates,
      previous: previousSummary
        ? { totals: previousSummary.totals, rates: previousSummary.rates }
        : null,
      recent,
      ai_sources,
      classification_version_current: CLASSIFICATION_VERSION,
    };
  });
